import React, { useState, useRef } from "react";
import { ShieldCheck, Upload, Loader2, FileCheck2 } from "lucide-react";
import { uploadComplianceCert } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { fmtDate } from "./PortalShared";

const color = { valid: "bg-green-100 text-green-700", "due-soon": "bg-amber-100 text-amber-700", expired: "bg-red-100 text-red-700" };

const Row = ({ item, onUpload, busy }) => {
  const ref = useRef(null);
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]" data-testid="landlord-compliance-row">
      <div>
        <p className="text-[14px] text-[#2b2b2b] flex items-center gap-2">{item.certUrl && <FileCheck2 size={15} className="text-green-600" />}{item.type}</p>
        <p className="text-[12px] text-[#a0a0a0]">{item.property} · expires {fmtDate(item.expires)}{item.certName ? ` · ${item.certName}` : ""}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${color[item.status] || color.valid}`}>{item.status.replace("-", " ")}</span>
        {item.certUrl && <a href={item.certUrl} target="_blank" rel="noreferrer" className="text-[12px] text-[#b8935f] hover:underline">View</a>}
        <button onClick={() => ref.current?.click()} disabled={busy} data-testid="compliance-upload-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
        </button>
        <input ref={ref} type="file" accept="image/*,application/pdf" hidden onChange={(e) => { if (e.target.files?.[0]) onUpload(item.id, e.target.files[0]); e.target.value = ""; }} />
      </div>
    </div>
  );
};

const LandlordCompliance = ({ items = [], onRefresh }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(null);
  const valid = items.filter((i) => i.status === "valid").length;
  const pct = items.length ? Math.round((valid / items.length) * 100) : 0;

  const upload = async (id, file) => {
    setBusy(id);
    try { await uploadComplianceCert(id, file); toast({ title: "Certificate uploaded", description: "Marked valid for 12 months." }); onRefresh && onRefresh(); }
    catch (e) { toast({ title: "Upload failed", description: e?.response?.data?.detail || "Please try again." }); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="landlord-compliance">
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] text-[#6a6a6a] flex items-center gap-2"><ShieldCheck size={16} className="text-[#b8935f]" /> Compliance progress</span>
          <span className="text-[13px] font-500 text-[#2b2b2b]" data-testid="compliance-progress">{valid}/{items.length} valid · {pct}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-[#f0ece5] overflow-hidden">
          <div className="h-full bg-[#b8935f] transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="space-y-2">
        {items.length === 0 ? <p className="text-[14px] text-[#8a8a8a]">No compliance items yet.</p> :
          items.map((i) => <Row key={i.id} item={i} onUpload={upload} busy={busy === i.id} />)}
      </div>
      <p className="text-[12px] text-[#a0a0a0] mt-4">Upload your EPC, Gas Safety (CP12) and EICR certificates. Files are stored securely and shared with your Anadear agent.</p>
    </div>
  );
};

export default LandlordCompliance;
